import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { GatewayDetails } from "@src/types/gateway-onboarding";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { DeviceSecurityMode } from "@src/types/device-security-mode";
import { DeviceSecurityPolicy } from "@src/types/device-security-policy";

export interface OpcUaSettingsState {
  authOption: DeviceAuthOption;
  securityMode: DeviceSecurityMode;
  securityPolicy: DeviceSecurityPolicy;
  username: string;
  password: string;
  clientCertificateBase64: string;
  clientCertificateBase64FileName: string;
  clientCertificatePrivateKeyBase64: string;
  clientCertificatePrivateKeyBase64FileName: string;
  clientCertificatePassword: string;
  serverPublicKeyBase64: string;
  serverPublicKeyBase64FileName: string;
  reuseLastAutogeneratedClientCertificate: boolean;
}

export const createDefaultOpcUaSettings = (): OpcUaSettingsState => ({
  authOption: DeviceAuthOption.ANONYMOUS,
  securityMode: DeviceSecurityMode.NONE,
  securityPolicy: DeviceSecurityPolicy.NONE,
  username: "",
  password: "",
  clientCertificateBase64: "",
  clientCertificateBase64FileName: "",
  clientCertificatePrivateKeyBase64: "",
  clientCertificatePrivateKeyBase64FileName: "",
  clientCertificatePassword: "",
  serverPublicKeyBase64: "",
  serverPublicKeyBase64FileName: "",
  reuseLastAutogeneratedClientCertificate: true,
});

export const getPrefillGateway = (
  onboardingGateway: GatewayDetails,
  connectedGatewaysListData: GatewayWithSourceViewStateTypes[],
): GatewayWithSourceViewStateTypes | null => {
  const prefilledGatewayFromList = onboardingGateway.gateway_id
    ? connectedGatewaysListData.find(
        (gateway) => gateway.gateway_uuid === onboardingGateway.gateway_id,
      ) || null
    : null;

  if (prefilledGatewayFromList) {
    return prefilledGatewayFromList;
  }

  if (!onboardingGateway.gateway_id) {
    return null;
  }

  return {
    gateway_uuid: onboardingGateway.gateway_id,
    name: onboardingGateway.name || onboardingGateway.gateway_id,
    vendor_id: onboardingGateway.vendor_id ?? "",
    sources: [],
    sources_visible: false,
  };
};

export const getMachineDrawerTexts = (i18n: (key: string) => string) => ({
  title: i18n("modules.addNewMachineDrawerModal.title"),
  titleStepTwo: i18n("modules.addNewMachineDrawerModal.titleStepTwo"),
  sectionTitle: i18n("modules.addNewMachineDrawerModal.sectionTitle"),
  sectionTitleStepTwo: i18n(
    "modules.addNewMachineDrawerModal.sectionTitleStepTwo",
  ),
  machineSkillsDescription: i18n(
    "modules.addNewMachineDrawerModal.machineSkillsDescription",
  ),
  stepGateway: i18n("modules.addNewMachineDrawerModal.stepGateway"),
  stepMachine: i18n("modules.addNewMachineDrawerModal.stepMachine"),
  stepThingsOs: i18n("modules.addNewMachineDrawerModal.stepThingsOs"),
  gatewayLabel: i18n("modules.addNewMachineDrawerModal.gatewayLabel"),
  gatewayPlaceholder: i18n(
    "modules.addNewMachineDrawerModal.gatewayPlaceholder",
  ),
  noGatewaysFound: i18n("modules.addNewMachineDrawerModal.noGatewaysFound"),
  machineNameLabel: i18n("modules.addNewMachineDrawerModal.machineNameLabel"),
  hostOrIpAddressLabel: i18n(
    "modules.addNewMachineDrawerModal.hostOrIpAddressLabel",
  ),
  help: i18n("modules.addNewMachineDrawerModal.help"),
  machineNameHelpText: i18n(
    "modules.addNewMachineDrawerModal.machineNameHelpText",
  ),
  ipAddressHelpText: i18n("modules.addNewMachineDrawerModal.ipAddressHelpText"),
  connectButton: i18n("modules.addNewMachineDrawerModal.connectButton"),
  continueMachineSkillsButton: i18n(
    "modules.addNewMachineDrawerModal.continueMachineSkillsButton",
  ),
  installAndFinishSetupButton: i18n(
    "modules.addNewMachineDrawerModal.installAndFinishSetupButton",
  ),
  finishSetupWithoutSkillsButton: i18n(
    "modules.addNewMachineDrawerModal.finishSetupWithoutSkillsButton",
  ),
  invalidIpAddressErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.invalidIpAddressErrorMessage",
  ),
  machineIpAddressMismatchErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.machineIpAddressMismatchErrorMessage",
  ),
  skillsNotAvailableErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.skillsNotAvailableErrorMessage",
  ),
  noMachineSkillsFoundMessage: i18n(
    "modules.addNewMachineDrawerModal.noMachineSkillsFoundMessage",
  ),
  opcUaSkillRequiredErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.opcUaSkillRequiredErrorMessage",
  ),
  operationPendingWarningMessage: i18n(
    "modules.addNewMachineDrawerModal.operationPendingWarningMessage",
  ),
  operationTimedOutErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.operationTimedOutErrorMessage",
  ),
  operationFailureErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.operationFailureErrorMessage",
  ),
  operationBackendFailureErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.operationBackendFailureErrorMessage",
  ),
  createMachineFailedErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.createMachineFailedErrorMessage",
  ),
  skillInstallSuccessMessage: i18n(
    "modules.addNewMachineDrawerModal.skillInstallSuccessMessage",
  ),
  invalidPortErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.invalidPortErrorMessage",
  ),
  invalidRpcDsnErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.invalidRpcDsnErrorMessage",
  ),
  titleSummary: i18n("modules.addNewMachineDrawerModal.titleSummary"),
  sectionTitleSummary: i18n(
    "modules.addNewMachineDrawerModal.sectionTitleSummary",
  ),
  summaryStatusLabel: i18n(
    "modules.addNewMachineDrawerModal.summaryStatusLabel",
  ),
  summaryInstalledSkillsLabel: i18n(
    "modules.addNewMachineDrawerModal.summaryInstalledSkillsLabel",
  ),
  summaryFailedSkillsLabel: i18n(
    "modules.addNewMachineDrawerModal.summaryFailedSkillsLabel",
  ),
  summarySkippedSkillsLabel: i18n(
    "modules.addNewMachineDrawerModal.summarySkippedSkillsLabel",
  ),
  summaryFinishButton: i18n(
    "modules.addNewMachineDrawerModal.summaryFinishButton",
  ),
  summaryGoToNewMachineButton: i18n(
    "modules.addNewMachineDrawerModal.summaryGoToNewMachineButton",
  ),
  summaryRetryButton: i18n(
    "modules.addNewMachineDrawerModal.summaryRetryButton",
  ),
  showProtocol: i18n("modules.addNewMachineDrawerModal.showProtocol"),
  hideProtocol: i18n("modules.addNewMachineDrawerModal.hideProtocol"),
  certificateGenerationFailureMessage: i18n(
    "modules.autoGenerateCertificateSetting.generatedInfoText",
  ),
  certificateGenerationFailedErrorMessage: i18n(
    "modules.addNewMachineDrawerModal.certificateGenerationFailedErrorMessage",
  ),
  summaryStatusSuccess: i18n(
    "modules.addNewMachineDrawerModal.summaryStatusSuccess",
  ),
  summaryStatusPartialSuccess: i18n(
    "modules.addNewMachineDrawerModal.summaryStatusPartialSuccess",
  ),
  summaryStatusFailed: i18n(
    "modules.addNewMachineDrawerModal.summaryStatusFailed",
  ),
  summaryStatusSkipped: i18n(
    "modules.addNewMachineDrawerModal.summaryStatusSkipped",
  ),
  opcUaErrorDetailsTitle: i18n(
    "modules.addNewMachineDrawerModal.opcUaErrorDetailsTitle",
  ),
});

export const getMachineSkillsTexts = (i18n: (key: string) => string) => ({
  thingsSupportTitle: i18n(
    "modules.addNewMachineDrawerModal.thingsSupportTitle",
  ),
  thingsSupportDescription: i18n(
    "modules.addNewMachineDrawerModal.thingsSupportDescription",
  ),
  thingsTwinTitle: i18n("modules.addNewMachineDrawerModal.thingsTwinTitle"),
  thingsTwinDescription: i18n(
    "modules.addNewMachineDrawerModal.thingsTwinDescription",
  ),
  opcUaRequiredTooltip: i18n(
    "modules.addNewMachineDrawerModal.opcUaRequiredTooltip",
  ),
  vncPortLabel: i18n("modules.addNewMachineDrawerModal.vncPortLabel"),
  vncPortHelpText: i18n("modules.addNewMachineDrawerModal.vncPortHelpText"),
  portSettingsLabel: i18n("modules.addNewMachineDrawerModal.portSettingsLabel"),
  portIsAvailableLabel: i18n(
    "modules.addNewMachineDrawerModal.portIsAvailableLabel",
  ),
  opcUaPortHelpText: i18n("modules.addNewMachineDrawerModal.opcUaPortHelpText"),
  rpcDsnLabel: i18n("modules.addNewMachineDrawerModal.rpcDsnLabel"),
  rpcPortHelpText: i18n("modules.addNewMachineDrawerModal.rpcPortHelpText"),
  securityModeLabel: i18n("modules.addNewMachineDrawerModal.securityModeLabel"),
  securityPolicyLabel: i18n(
    "modules.addNewMachineDrawerModal.securityPolicyLabel",
  ),
  authenticationTabsLabel: i18n(
    "modules.addNewMachineDrawerModal.authenticationTabsLabel",
  ),
  usernameLabel: i18n("modules.addNewMachineDrawerModal.usernameLabel"),
  passwordLabel: i18n("modules.addNewMachineDrawerModal.passwordLabel"),
  clientCertificateLabel: i18n(
    "modules.addNewMachineDrawerModal.clientCertificateLabel",
  ),
  clientCertificatePrivateKeyLabel: i18n(
    "modules.addNewMachineDrawerModal.clientCertificatePrivateKeyLabel",
  ),
  clientCertificatePasswordLabel: i18n(
    "modules.addNewMachineDrawerModal.clientCertificatePasswordLabel",
  ),
  serverPublicKeyLabel: i18n(
    "modules.addNewMachineDrawerModal.serverPublicKeyLabel",
  ),
  anonymousTab: i18n("modules.addNewMachineDrawerModal.anonymousTab"),
  userLoginTab: i18n("modules.addNewMachineDrawerModal.userLoginTab"),
  clientCertificateTab: i18n(
    "modules.addNewMachineDrawerModal.clientCertificateTab",
  ),
  pleaseFixFieldsLabel: i18n(
    "modules.addNewMachineDrawerModal.pleaseFixFieldsLabel",
  ),
});
